import React, { useMemo, useRef, useState } from 'react';
import SortableList from '@shared/components/SortableList';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useThrottledWindowResize } from '@shared/utils/hooks/useThrottledWindowResize';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { noop } from '@shared/utils/noop';
import { SkeletonGroup } from '@shared/components/Skeleton/Skeleton';
import { HotelTileSkeleton } from '@shared/components/HotelTile/HotelTile.skeleton';
import { StayAreaTile } from './StayAreaTile';
import type { StayAreaData } from '@shared/components/StayAreaTile/StayAreaTile.types';

type StayAreasListProps = Readonly<{
  loading: boolean;
  stayAreas: ReadonlyArray<StayAreaData>;
  onRemove: (stayArea: StayAreaData) => void;
}>;

export const StayAreasList: React.FC<StayAreasListProps> = ({ loading, stayAreas, onRemove }) => {
  const { setCustomComponentBlockData } = useCustomComponentStateContext();
  const { navigateToScreen } = useNavigationPaneContext();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const measureWidth = useEventCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  });

  useThrottledWindowResize(measureWidth);

  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragStart = useEventCallback(() => {
    setIsDragActive(true);
  });

  const handleDragCancel = useEventCallback(() => {
    setIsDragActive(false);
  });

  // @todo [TRAV-2250] Implement reorder mutation when backend supports it
  const handleDragEnd = useEventCallback(() => {
    setIsDragActive(false);
  });

  const handleStayAreaEdit = useEventCallback((stayArea: StayAreaData) => {
    setCustomComponentBlockData(stayArea);
    navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_STAY_AREA_EDIT);
  });

  const mutableStayAreas = useMemo(() => Array.from(stayAreas), [stayAreas]);

  return (
    <SkeletonGroup
      rootRef={containerRef}
      isReady={!loading}
      placeholder={<HotelTileSkeleton />}
      display="flex"
      flexDirection="column"
      gap={7}
    >
      <SortableList
        items={mutableStayAreas}
        renderItem={stayArea => (
          <StayAreaTile
            key={stayArea.id}
            stayArea={stayArea}
            isDragActive={isDragActive}
            containerWidth={containerWidth}
            onEdit={handleStayAreaEdit}
            onRemove={onRemove}
          />
        )}
        onChange={noop}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
        adjustOverlayScale={false}
      />
    </SkeletonGroup>
  );
};
